import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShieldCheck, Sparkles, Check, Calendar, Clock, MapPin, Users, Crown, AlertCircle, Ticket } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";
import useSurcharge from "../hooks/useSurcharge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOLD = "#D4AF37";
const CHAMPAGNE = "#F5E6C8";

export default function BookNYECruise() {
 const [params] = useSearchParams();
 const [availability, setAvailability] = useState(null);
 const [loading, setLoading] = useState(true);
 const [step, setStep] = useState(1);
 const [picked, setPicked] = useState({ adult_qty: 2, child_qty: 0 });
 const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
 const [submitting, setSubmitting] = useState(false);

 useEffect(() => { document.title = "Buy NYE 2026 Cruise Tickets | Magic Cruises"; }, []);

 useEffect(() => {
  if (params.get("cancelled")) toast.info("Payment cancelled, your NYE seats were not booked.");
 }, [params]);

 useEffect(() => {
  (async () => {
   try {
    const r = await axios.get(`${API}/nye/availability`);
    setAvailability(r.data);
   } catch (e) {
    toast.error("Could not load live NYE availability. Please refresh.");
   } finally { setLoading(false); }
  })();
 }, []);

 const totalQty = picked.adult_qty + picked.child_qty;
 const adultPrice = availability?.pricing.adult ?? 1250;
 const childPrice = availability?.pricing.child ?? 950;
 const subtotal = (picked.adult_qty * adultPrice) + (picked.child_qty * childPrice);
 const { cfg: surchargeCfg, apply: applySurcharge } = useSurcharge();
 const { surcharge, total } = applySurcharge(subtotal);
 const remaining = availability?.remaining ?? 120;
 const soldOut = availability?.status === "sold_out";

 const canStep1 = totalQty > 0 && totalQty <= remaining;
 const canStep2 = customer.name.trim().length >= 2 && /.+@.+\..+/.test(customer.email);

 const startCheckout = async () => {
  if (!canStep2) { toast.error("Please complete your name and email"); return; }
  setSubmitting(true);
  try {
   const r = await axios.post(`${API}/checkout/nye`, {
    adult_qty: picked.adult_qty, child_qty: picked.child_qty,
    customer_name: customer.name, customer_email: customer.email, customer_phone: customer.phone,
    origin_url: window.location.origin,
   });
   window.location.href = r.data.url;
  } catch (e) {
   toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
   setSubmitting(false);
  }
 };

 const StepDot = ({ n, label }) => (
  <div className="flex items-center gap-2.5 shrink-0">
   <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-black ${step === n ? "bg-amber-300 text-[#0A0A0A] shadow-lg shadow-amber-300/40" : step > n ? "bg-amber-300/30 text-amber-200" : "bg-white/10 text-white/50"}`}>{step > n ? <Check className="w-4 h-4" strokeWidth={3} /> : n}</span>
   <span className={`text-xs uppercase tracking-[0.22em] font-bold hidden sm:inline ${step === n ? "text-white" : "text-white/60"}`}>{label}</span>
  </div>
 );

 return (
  <main className="bg-[#06060A] text-white min-h-screen relative overflow-hidden" data-testid="nye-book-page">
   <SEOHead title="Buy NYE 2026 Cruise Tickets | Magic Cruises" description="Buy Sydney Harbour NYE 2026 cruise tickets online. Adult $1,250 / Child $950. 6 hours of cruising, front-row 9pm + midnight fireworks, premium open bar, live DJ. Boarding 7:30pm King St Wharf." canonical="https://magiccruises.com.au/cruises/nye-cruises-sydney-harbour/book" />

   {/* Atmospheric bg */}
   <div className="fixed inset-0 pointer-events-none">
    <div className="absolute top-0 left-1/3 w-[40rem] h-[40rem] rounded-full bg-amber-400/15 blur-3xl animate-pulse" />
    <div className="absolute bottom-0 right-1/4 w-[36rem] h-[36rem] rounded-full bg-fuchsia-500/12 blur-3xl animate-pulse" style={{ animationDelay: "2s" }} />
   </div>
   <Starfield density={70} color="#FFE9A8" glow="rgba(255,210,120,0.6)" fixed={true} intensity={0.6} />

   <div className="relative pt-28 pb-32 px-5 lg:px-10 mx-auto max-w-6xl">
    <Link to="/cruises/nye-cruises-sydney-harbour" className="inline-flex items-center gap-2 text-white/65 hover:text-amber-300 text-xs font-bold uppercase tracking-[0.28em] mb-7 transition-colors" data-testid="nye-book-back-link">
     <ArrowLeft className="w-3.5 h-3.5" /> Back to NYE Cruise
    </Link>

    <div className="flex items-center gap-2 text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-4">
     <Ticket className="w-3.5 h-3.5" /> NYE 2026 · 31 December · King St Wharf
    </div>
    <h1 className="font-black tracking-tight leading-[0.95] mb-2" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", fontFamily: "'Cinzel Decorative', serif" }}>
     Buy your <em className="font-light italic" style={{ color: GOLD }}>NYE 2026 ticket.</em>
    </h1>
    <p className="text-white/70 text-base lg:text-lg mt-3 mb-10 max-w-2xl leading-[1.7]">Sydney's most premium NYE cruise. Adult $1,250 · Child $950 all inclusive. Boards King St Wharf 7:30pm, returns ~1:30am after the midnight fireworks.</p>

    {/* Step bar */}
    <div className="flex items-center gap-3 sm:gap-5 overflow-x-auto pb-3 mb-10 -mx-2 px-2" data-testid="nye-book-steps">
     <StepDot n={1} label="Tickets" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={2} label="Your details" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={3} label="Pay" />
    </div>

    {loading && <div className="text-white/65 text-center py-20">Loading live NYE availability…</div>}

    {!loading && soldOut && (
     <div className="rounded-3xl ring-1 ring-red-400/30 bg-red-500/10 p-8 lg:p-10 text-center" data-testid="nye-sold-out">
      <AlertCircle className="w-10 h-10 text-red-300 mx-auto" />
      <h2 className="font-black text-3xl text-white mt-4">NYE 2026 is sold out.</h2>
      <p className="text-white/75 text-base mt-3 max-w-lg mx-auto">Last NYE we sold out by mid-October. Drop your details below and we'll prioritise you for the NYE 2027 release in February.</p>
      <Link to="/cruises/nye-cruises-sydney-harbour#enquiry" className="inline-flex items-center gap-2 mt-6 px-7 py-3.5 rounded-full bg-amber-300 text-[#0A0A0A] font-black text-xs uppercase tracking-[0.22em]">
       Join the NYE 2027 waitlist <ArrowRight className="w-4 h-4" />
      </Link>
     </div>
    )}

    {!loading && !soldOut && availability && (
     <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
      {/* MAIN STEP */}
      <div className="lg:col-span-8">
       {step === 1 && (
        <div data-testid="nye-step-tickets">
         <h2 className="text-3xl md:text-4xl font-black mb-3">1. How many tickets?</h2>
         <p className="text-white/85 text-lg mb-8 leading-[1.7]">Up to 20 tickets per booking. For groups of 10+ please <Link to="/cruises/nye-cruises-sydney-harbour#enquiry" className="text-amber-300 underline">enquire about a group booking</Link> (deposit option available).</p>
         <div className="space-y-4">
          {[
           { k: "adult_qty", label: "Adult", price: adultPrice, hint: "13+ years · all inclusive" },
           { k: "child_qty", label: "Child", price: childPrice, hint: "12 & under · all inclusive" }].map(row => (
           <div key={row.k} className="flex items-center justify-between gap-4 p-6 rounded-2xl ring-1 ring-white/10 bg-white/[0.04]" data-testid={`nye-row-${row.k}`}>
            <div>
             <div className="text-white text-2xl font-black">{row.label}</div>
             <div className="text-white/70 text-sm uppercase tracking-widest font-bold mt-1.5">{row.hint} · ${row.price.toLocaleString()} ea</div>
            </div>
            <div className="flex items-center gap-4">
             <button onClick={() => setPicked(p => ({ ...p, [row.k]: Math.max(0, p[row.k] - 1) }))} className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl font-black transition-colors" data-testid={`nye-qty-${row.k}-minus`}>−</button>
             <span className="w-12 text-center text-white text-3xl font-black tabular-nums" data-testid={`nye-qty-${row.k}`}>{picked[row.k]}</span>
             <button onClick={() => setPicked(p => ({ ...p, [row.k]: Math.min(20, p[row.k] + 1) }))} className="w-12 h-12 rounded-full bg-amber-300 hover:bg-amber-200 text-[#0A0A0A] text-2xl font-black transition-colors" data-testid={`nye-qty-${row.k}-plus`}>+</button>
            </div>
           </div>
          ))}
          {totalQty > remaining && (
           <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-base" data-testid="nye-qty-error">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>Only {remaining} NYE seats remain. Reduce your quantity to continue.</div>
           </div>
          )}
          {remaining > 0 && remaining <= 30 && (
           <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-400/30 text-amber-200 text-base font-bold" data-testid="nye-low-warning">
            <Sparkles className="w-5 h-5 shrink-0 mt-0.5" />
            <div>Only {remaining} NYE tickets left. Selling fast.</div>
           </div>
          )}
         </div>
        </div>
       )}

       {step === 2 && (
        <div data-testid="nye-step-customer">
         <h2 className="text-3xl md:text-4xl font-black mb-3">2. Your details</h2>
         <p className="text-white/85 text-lg mb-8 leading-[1.7]">We'll email your tickets and NYE boarding pass to this address.</p>
         <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2"><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Full name *</label><input data-testid="nye-cust-name" value={customer.name} onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:border-amber-300 focus:bg-white/10 outline-none text-white text-lg" placeholder="Your full name" /></div>
          <div><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Email *</label><input data-testid="nye-cust-email" type="email" value={customer.email} onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:border-amber-300 focus:bg-white/10 outline-none text-white text-lg" placeholder="you@email.com" /></div>
          <div><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Phone</label><input data-testid="nye-cust-phone" value={customer.phone} onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:border-amber-300 focus:bg-white/10 outline-none text-white text-lg" placeholder="0400…" /></div>
         </div>
         <div className="flex items-center gap-2 mt-8 text-white/82 text-sm">
          <ShieldCheck className="w-4 h-4 text-amber-300" />
          Secure payment via Stripe · Cards in AUD · Tickets emailed instantly after payment
         </div>
        </div>
       )}

       {/* Nav buttons */}
       <div className="flex justify-between gap-4 mt-12 pb-24 lg:pb-0">
        <button disabled={step === 1} onClick={() => setStep(s => Math.max(1, s - 1))} className="px-7 py-5 rounded-full bg-white/5 hover:bg-white/10 text-white text-sm font-black uppercase tracking-[0.22em] disabled:opacity-30 disabled:cursor-not-allowed" data-testid="nye-step-prev">
         <ArrowLeft className="w-4 h-4 inline mr-2" /> Back
        </button>
        {step < 2 ? (
         <button disabled={!canStep1} onClick={() => setStep(2)} className="px-10 py-5 rounded-full text-[#0A0A0A] text-sm font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)] transition-all" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="nye-step-next">
          Continue <ArrowRight className="w-4 h-4 inline ml-2" />
         </button>
        ) : (
         <button disabled={!canStep2 || submitting} onClick={startCheckout} className="px-10 py-5 rounded-full text-[#0A0A0A] text-sm font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)] transition-all" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="nye-step-checkout">
          {submitting ? "Redirecting to Stripe…" : <>Pay ${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} AUD <Sparkles className="w-4 h-4 inline ml-2" /></>}
         </button>
        )}
       </div>
      </div>

      {/* SUMMARY SIDEBAR */}
      <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start" data-testid="nye-book-summary">
       <div className="rounded-3xl ring-1 ring-amber-300/30 bg-gradient-to-br from-amber-400/[0.12] via-[#1A1108]/80 to-[#0A0618]/80 backdrop-blur-md p-7 lg:p-8 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.35)]">
        <div className="text-amber-300 text-xs uppercase tracking-[0.32em] font-bold mb-5">Your NYE 2026 cruise</div>
        <dl className="space-y-4 text-base">
         <Row label="Date" value="31 Dec 2026 · Thursday" icon={Calendar} />
         <Row label="Boarding" value="7:30pm" icon={Clock} />
         <Row label="Returns" value="~1:30am" icon={Clock} />
         <Row label="Wharf" value="King St Wharf 3" icon={MapPin} />
         <Row label="Tickets" value={totalQty > 0 ? `${picked.adult_qty} adult · ${picked.child_qty} child` : ""} icon={Users} />
        </dl>
        <div className="border-t border-white/15 mt-6 pt-6 space-y-3">
         <div className="flex justify-between items-baseline text-white/85 text-base">
          <span><span className="font-black text-white">{picked.adult_qty}</span> × Adult <span className="text-white/50 text-sm">(${adultPrice.toLocaleString()} ea)</span></span>
          <span className="text-lg font-bold tabular-nums">${(picked.adult_qty * adultPrice).toLocaleString()}</span>
         </div>
         <div className="flex justify-between items-baseline text-white/85 text-base">
          <span><span className="font-black text-white">{picked.child_qty}</span> × Child <span className="text-white/50 text-sm">(${childPrice.toLocaleString()} ea)</span></span>
          <span className="text-lg font-bold tabular-nums">${(picked.child_qty * childPrice).toLocaleString()}</span>
         </div>
         {surchargeCfg.enabled && surcharge > 0 && (
          <div className="flex justify-between items-baseline pt-3 mt-2 border-t border-white/10 text-white/75 text-sm">
           <span>{surchargeCfg.label}</span>
           <span className="font-bold tabular-nums">+${surcharge.toFixed(2)}</span>
          </div>
         )}
         <div className="flex justify-between items-baseline pt-4 mt-3 border-t border-white/15">
          <span className="text-white text-2xl font-black">Total AUD</span>
          <span className="text-4xl font-black tabular-nums bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }}>${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
         </div>
         <div className="text-white/55 text-[11px] uppercase tracking-widest font-bold pt-1">GST inclusive · Free cancellation up to 72hrs prior</div>
        </div>
        <ul className="mt-7 space-y-2.5 text-white/82 text-sm">
         <li className="flex items-start gap-2.5"><Crown className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" /> Front-row 9pm + midnight fireworks (exclusion zone)</li>
         <li className="flex items-start gap-2.5"><Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" /> Premium open bar · Wolf Blass, Carlton Dry, Cuvée</li>
         <li className="flex items-start gap-2.5"><Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" /> Canapés + standing buffet + dessert</li>
         <li className="flex items-start gap-2.5"><Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" /> Live DJ + dance floor all night</li>
         <li className="flex items-start gap-2.5"><Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" /> Tickets emailed instantly after payment</li>
        </ul>
       </div>
      </aside>
     </div>
    )}
   </div>

   {/* Sticky bottom CTA bar , always in view */}
   <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0A0618]/95 backdrop-blur-xl border-t border-amber-300/25 shadow-[0_-15px_45px_-10px_rgba(0,0,0,0.7)]" data-testid="sticky-cta-bar">
    <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-3">
     <div className="hidden sm:block flex-1 min-w-0">
      <div className="text-amber-300 text-[10px] uppercase tracking-[0.28em] font-bold">Step {step} of 2{step === 2 ? " · Final" : ""}</div>
      <div className="text-white text-sm font-bold truncate">
       {step === 1 && "Choose your NYE tickets"}
       {step === 2 && `Pay $${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} AUD · ${totalQty} ticket${totalQty !== 1 ? "s" : ""}`}
      </div>
     </div>
     <div className="sm:hidden flex-1 min-w-0">
      <div className="text-amber-300 text-[10px] uppercase tracking-[0.28em] font-bold">Step {step}/2</div>
      <div className="text-white text-xs font-bold truncate">{step === 2 ? `$${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} · ${totalQty} ticket${totalQty !== 1 ? "s" : ""}` : "NYE 2026"}</div>
     </div>
     <button disabled={step === 1} onClick={() => setStep(s => Math.max(1, s - 1))} className="px-4 py-3 rounded-full bg-white/8 hover:bg-white/15 text-white text-xs font-black uppercase tracking-[0.22em] disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap" data-testid="sticky-prev">
      <ArrowLeft className="w-3.5 h-3.5 inline sm:mr-1.5" /><span className="hidden sm:inline">Back</span>
     </button>
     {step < 2 ? (
      <button disabled={!canStep1} onClick={() => setStep(2)} className="px-6 sm:px-8 py-3 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-amber-500/40 transition-all whitespace-nowrap" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="sticky-next">
       Continue <ArrowRight className="w-3.5 h-3.5 inline ml-1.5" />
      </button>
     ) : (
      <button disabled={!canStep2 || submitting} onClick={startCheckout} className="px-6 sm:px-8 py-3 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-amber-500/40 transition-all whitespace-nowrap" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="sticky-checkout">
       {submitting ? "Redirecting…" : <>Pay ${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} <Sparkles className="w-3.5 h-3.5 inline ml-1.5" /></>}
      </button>
     )}
    </div>
   </div>
  </main>
 );
}

function Row({ label, value, icon: Icon }) {
 return (
  <div className="flex items-start gap-3">
   <Icon className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
   <div className="flex-1 flex justify-between items-baseline gap-3 flex-wrap">
    <span className="text-white/60 uppercase tracking-widest text-[11px] font-bold">{label}</span>
    <span className="text-white text-right font-bold text-base">{value}</span>
   </div>
  </div>
 );
}
